import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import { QueryState } from "@/components/common/AsyncState";
import { KpiCard } from "@/components/common/KpiCard";
import { AcceptPaymentDialog } from "@/components/finance/AcceptPaymentDialog";
import { fetchPayments } from "@/api/finance";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import { PAYMENT_METHOD_LABELS } from "@/types/enums";
import type { PaymentMethod } from "@/types/enums";
import { formatDate, formatMoney, periodLabel } from "@/utils/format";

interface Props {
  /** Период вида YYYY-MM; если не задан, показываются последние платежи. */
  period?: string;
}

function periodRange(period?: string): { from?: string; to?: string } {
  if (!period) return {};
  const [year, month] = period.split("-").map(Number);
  if (!year || !month) return {};
  const from = new Date(year, month - 1, 1);
  const to = new Date(year, month, 0);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

export function PaymentsTab({ period }: Props = {}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [method, setMethod] = useState<PaymentMethod | "">("");
  const range = periodRange(period);
  const { data, isLoading, error } = useQuery({
    queryKey: ["payments", period, method],
    queryFn: () => fetchPayments({ limit: 100, method: method || undefined, ...range }),
  });

  const payments = data?.items ?? [];
  const total = payments.reduce((sum, p) => sum + p.amount, 0);
  const averageTicket = payments.length ? total / payments.length : 0;
  const byMethod = new Map<string, number>();
  for (const p of payments) byMethod.set(p.method, (byMethod.get(p.method) ?? 0) + p.amount);
  const topMethod = Array.from(byMethod.entries()).sort((a, b) => b[1] - a[1])[0];

  return (
    <>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Поступило" value={formatMoney(total)} hint={`${payments.length} платежей`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Средний чек" value={payments.length ? formatMoney(averageTicket) : "—"} hint="за один платёж" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Основной способ оплаты"
            value={topMethod ? PAYMENT_METHOD_LABELS[topMethod[0] as keyof typeof PAYMENT_METHOD_LABELS] : "—"}
            hint={topMethod ? formatMoney(topMethod[1]) : undefined}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Всего платежей" value={String(payments.length)} />
        </Grid>
      </Grid>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} mb={2}>
        <TextField
          select
          size="small"
          label="Тип оплаты"
          value={method}
          onChange={(e) => setMethod(e.target.value as PaymentMethod | "")}
          sx={{ minWidth: 190 }}
        >
          <MenuItem value="">Все типы оплаты</MenuItem>
          {(Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethod[]).map((key) => (
            <MenuItem key={key} value={key}>
              {PAYMENT_METHOD_LABELS[key]}
            </MenuItem>
          ))}
        </TextField>
        {method && (
          <Button size="small" onClick={() => setMethod("")}>
            Сбросить
          </Button>
        )}
        <Box flexGrow={1} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          Принять оплату
        </Button>
      </Stack>
      <QueryState isLoading={isLoading} error={error}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Дата</TableCell>
              <TableCell>Ученик</TableCell>
              <TableCell>Группа</TableCell>
              <TableCell>Период</TableCell>
              <TableCell>Сумма</TableCell>
              <TableCell>Способ</TableCell>
              <TableCell>Принял</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.items ?? []).map((payment) => (
              <TableRow key={payment.id}>
                <TableCell>{formatDate(payment.paidAt)}</TableCell>
                <TableCell>{payment.studentName ?? payment.studentId}</TableCell>
                <TableCell>{payment.groupCode ?? payment.groupId}</TableCell>
                <TableCell>{periodLabel(payment.period)}</TableCell>
                <TableCell>{formatMoney(payment.amount)}</TableCell>
                <TableCell>{PAYMENT_METHOD_LABELS[payment.method]}</TableCell>
                <TableCell>{payment.receivedByUserName ?? "—"}</TableCell>
              </TableRow>
            ))}
            {(data?.items ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Оплат за выбранный период нет.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </QueryState>
      <AcceptPaymentDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  );
}
